/**
 * HTML utility functions for template processing.
 * Used by CadenceStep components for @ variable and / document interpolation.
 */

/**
 * Type guard to check if a string is not null, undefined, or empty.
 */
const isNotEmpty = (arg: string | undefined | null): arg is string => {
  return arg != null && arg.length > 0;
};

// ============================================================================
// Types
// ============================================================================

/**
 * Document type for template interpolation.
 * Compatible with both SvrDocument and simplified EditorDocument.
 */
export type TemplateDocument = {
  id: string;
  title?: string | null;
  fileUrl?: string | null;
  ogImageUrl?: string | null;
  isPrivate?: boolean;
};

// ============================================================================
// Functions
// ============================================================================

/**
 * Extracts plain text from HTML content.
 * @param html - HTML string to extract text from
 * @param insertSpacesBetweenPTags - Whether to add spaces between <p> tags
 * @returns Plain text with non-printable characters removed
 */
export const getTextFromHtml = (html: string, insertSpacesBetweenPTags?: boolean): string => {
  if (html === '<p><br></p>') return '';
  if (html === '<div><br></div>') return '';
  let newHtml = html;
  const domParser = new DOMParser();
  if (insertSpacesBetweenPTags) {
    const document = domParser.parseFromString(newHtml, 'text/html');
    const tags = document.querySelectorAll('p');
    tags.forEach((el) => {
      if (!el.innerHTML.endsWith(' ')) {
        el.innerHTML += ' ';
      }
    });
    newHtml = document.body.innerHTML;
  }
  newHtml = newHtml?.replace(/<br\s*\/?>/gm, '\n');
  const document = domParser.parseFromString(newHtml, 'text/html');
  return document.body.textContent?.replace(/[^\x20-\x7E]+/g, '') || '';
};

/**
 * Converts hardcoded HTML content back to template format.
 * Removes span elements that were added during template processing.
 * @param html - HTML string with hardcoded values
 * @returns HTML string with spans removed
 */
export const convertHardcodedHtmlToTemplate = (html: string): string => {
  if (!html) return html;
  const spanRegex = /(<span[^>]*>.*?<\/span>)/g;
  const htmlParts = html.split(spanRegex) || [];
  return htmlParts.join('');
};

/**
 * Checks if a TipTap editor string has actual content.
 * An empty TipTap editor contains '<p></p>' which should be treated as empty.
 * @param s - TipTap HTML content
 * @returns true if the content is not empty
 */
export const isNotEmptyTipTap = (s: string | undefined | null): boolean => {
  return isNotEmpty(s) && s !== '<p></p>';
};

/**
 * Cleans text by treating '<p></p>' and empty strings as undefined.
 * @param text - Input text
 * @returns Cleaned text or undefined if empty
 */
export const cleanText = (text: string | undefined): string | undefined => {
  if (text == null) return undefined;
  if (text === '<p></p>' || text.length === 0) return '';
  return text;
};

type ReplaceHtmlError = [string, boolean];

/**
 * Converts template HTML to display HTML by replacing @ variables and / documents
 * with their actual values.
 *
 * @param html - Template HTML containing data-id attributes for variables
 * @param atVariables - Map of variable names to their values (e.g., { FirstName: 'John' })
 * @param documents - Array of documents for / document references
 * @param hideError - If true, don't mark unresolved variables as errors
 * @returns Tuple of [processedHtml, hasError]
 */
export const convertTemplateToHtml = (
  html: string,
  atVariables?: Record<string, string>,
  documents?: TemplateDocument[],
  hideError?: boolean,
): ReplaceHtmlError => {
  const div = document.createElement('div');
  div.innerHTML = html;

  let hasError = false; // check has forbidden to send variables

  const confirmError = () => {
    if (!hideError) {
      hasError = true;
    }
  };

  div.querySelectorAll('[data-id]').forEach((d) => {
    const id = d.getAttribute('data-id');
    if (id == null) {
      // unhandled variables
      return;
    }

    let type: string | undefined = undefined; // should be equal to name of mention extension on tiptap
    let value: string | undefined = undefined;
    let fileUrl: string | undefined = undefined;
    let imageUrl: string | undefined = undefined;

    const startValue = d.getAttribute('data-label');
    const doc = documents?.find((doc) => doc.id === id);
    const atVariable = atVariables?.[id];
    const isComment = id === '{Comment}';
    const isDocumentType = id.startsWith('doc_');

    if (atVariable) {
      type = 'atVariables'; // defined atVariables
      value = atVariable;
    } else if (doc != null && !doc.isPrivate) {
      type = 'documents'; // defined documents
      value = `${doc.title ?? 'Document'}`;
      fileUrl = doc.fileUrl ?? undefined;
      imageUrl = doc.ogImageUrl ?? undefined;
    } else if (doc != null && doc.isPrivate) {
      type = 'documents'; // defined documents, but private
      value = '{private_content}';
      confirmError();
    } else if (isComment) {
      type = 'comments'; // all the comments
      value = startValue ?? '{Comment}';
      confirmError();
    } else if (isDocumentType) {
      type = 'documents'; // undefined documents
      value = '{document_not_found}';
      confirmError();
    } else {
      type = 'atVariables'; // undefined atVariables
      value = `{${id}}`;
      confirmError();
    }

    d.innerHTML = value;
    d.setAttribute('data-type', type);
    d.setAttribute('data-label', value);
    if (fileUrl) {
      d.setAttribute('data-file-url', fileUrl);
    } else {
      d.removeAttribute('data-file-url');
    }
    if (imageUrl) {
      d.setAttribute('data-image-url', imageUrl);
    } else {
      d.removeAttribute('data-image-url');
    }
  });
  return [div.innerHTML, hasError];
};

/**
 * Fake @ variables for preview mode when real contact data isn't available.
 */
export const fakeAtVariables: Record<string, string> = {
  FirstName: '{FirstName}',
  LastName: '{LastName}',
  Title: '{Title}',
  Company: '{Company}',
  City: '{City}',
};

/**
 * Fake @ variables for AI generation when real contact data isn't available.
 */
export const aiFakeAtVariables: Record<string, string> = {
  FirstName: '{FirstName}',
  LastName: '{LastName}',
  Title: '{Title}',
  Company: '{Company}',
  City: '{City}',
  Headline: '{Headline}',
  About: '{About}',
};
